import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { DashSidebar } from "@/components/DashSidebar";
import ResumePreview from "@/components/resume/ResumePreview";
import {
  ArrowLeft, Sparkle, FloppyDisk, DownloadSimple, Plus, X, MagicWand,
  Briefcase, GraduationCap, Wrench, Certificate, Code, Translate, Target,
  ChartLineUp, Eye, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import UpgradeGate from "@/components/UpgradeGate";
import { hasPlan } from "@/lib/planAccess";
import {
  getResume, updateResume, aiGenerateSummary, aiGenerateBullets, aiSuggestSkills,
  aiImproveText, scoreATS, optimizeForJob, getLatestATS, getResumeHistory, getATSTrend, downloadResumeDOCX,
  blankExperience, blankEducation, blankCertification, blankProject, blankLanguage,
  TEMPLATE_META,
} from "@/lib/resumeApi";
import { downloadResumePDF } from "@/lib/resumePdf";

const SECTIONS = [
  { id: "personal", label: "Personal" },
  { id: "summary", label: "Summary" },
  { id: "experience", label: "Experience" },
  { id: "education", label: "Education" },
  { id: "skills", label: "Skills" },
  { id: "certifications", label: "Certifications" },
  { id: "projects", label: "Projects" },
  { id: "languages", label: "Languages" },
];

export default function ResumeEditor() {
  const { id } = useParams();
  const { user } = useAuth();
  const paid = hasPlan(user, "pro");
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resume, setResume] = useState(null);
  const [activeSection, setActiveSection] =
    useState("personal");
  const [mobilePreview, setMobilePreview] =
    useState(false);
  const [atsBusy, setAtsBusy] =
    useState(false);
  const [atsResult, setAtsResult] =
    useState(null);
  const [showJD, setShowJD] =
    useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [atsTrend, setAtsTrend] = useState([]);

  // Load resume
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const r = await getResume(id);

        if (alive) {
          setResume(r);

          getLatestATS(id)
            .then((ats) => {
              if (
                alive &&
                ats?.ats_score != null
              ) {
                setAtsResult(ats);

                setResume((prev) =>
                  prev
                    ? {
                        ...prev,
                        ats_score:
                          ats.ats_score,
                      }
                    : prev
                );
              }
            })
            .catch(() => {});
        }
      } catch (err) {
        console.error(
          "load failed",
          err
        );

        toast.error(
          "Couldn't load resume"
        );

        nav("/resume-builder");
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [id, nav]);

  // Debounced autosave
  const dirtyRef =
    useRef(false);

  const saveTimer =
    useRef(null);

  const lastSnapshot =
    useRef("");

  useEffect(() => {
    if (!resume) {
      return;
    }

    const snap =
      JSON.stringify({
        name: resume.name,
        template: resume.template,
        data: resume.data,
      });

    if (
      snap ===
      lastSnapshot.current
    ) {
      return;
    }

    lastSnapshot.current =
      snap;

    dirtyRef.current =
      true;

    clearTimeout(
      saveTimer.current
    );

    saveTimer.current =
      setTimeout(
        async () => {
          setSaving(true);

          try {
            const updated =
              await updateResume(
                resume.id,
                {
                  name: resume.name,
                  template:
                    resume.template,
                  data: resume.data,
                }
              );

            setResume(
              (prev) => ({
                ...prev,
                updated_at:
                  updated.updated_at,
              })
            );

            dirtyRef.current =
              false;
          } catch (err) {
            console.error(
              "autosave failed",
              err
            );

            toast.error(
              "Autosave failed"
            );
          } finally {
            setSaving(false);
          }
        },
        900
      );

    return () =>
      clearTimeout(
        saveTimer.current
      );
  }, [resume]);

  const update = useCallback(
    (patch) =>
      setResume((r) => ({
        ...r,
        ...patch,
      })),
    []
  );

  const updateData =
    useCallback(
      (patch) =>
        setResume((r) => ({
          ...r,
          data: {
            ...r.data,
            ...patch,
          },
        })),
      []
    );

  /*
   * PERFORMANCE FIX
   *
   * The editor state updates immediately.
   * The heavy A4 preview receives a deferred
   * version so typing does not wait for the
   * complete preview to redraw.
   */
  const previewResume =
    useDeferredValue(
      resume
    );

  /*
   * PREVIEW SIZE FIX
   *
   * The old preview used transform: scale()
   * directly on the A4 document.
   *
   * CSS still reserved the original 210mm
   * width, which caused the right side to be
   * clipped.
   *
   * We now calculate the available width and
   * create a correctly sized outer frame.
   */
  const previewViewportRef =
    useRef(null);

  const [
    previewScale,
    setPreviewScale,
  ] = useState(0.72);

  useEffect(() => {
    const element =
      previewViewportRef.current;

    if (!element) {
      return undefined;
    }

    const calculateScale =
      () => {
        const availableWidth =
          Math.max(
            260,
            element.clientWidth -
              24
          );

        // 1mm ≈ 3.7795 CSS pixels.
        const a4WidthPx =
          210 *
          3.779527559;

        const calculated =
          availableWidth /
          a4WidthPx;

        /*
         * Keep the preview readable
         * while guaranteeing that it fits.
         */
        const scale =
          Math.min(
            0.78,
            Math.max(
              0.36,
              calculated
            )
          );

        setPreviewScale(
          Number(
            scale.toFixed(4)
          )
        );
      };

    calculateScale();

    if (
      typeof ResizeObserver ===
      "undefined"
    ) {
      window.addEventListener(
        "resize",
        calculateScale
      );

      return () =>
        window.removeEventListener(
          "resize",
          calculateScale
        );
    }

    const observer =
      new ResizeObserver(
        calculateScale
      );

    observer.observe(
      element
    );

    return () =>
      observer.disconnect();
  }, []);

  const openHistory = useCallback(async () => {
    if (!paid) return;
    try {
      const [h, t] = await Promise.all([getResumeHistory(id), getATSTrend(id)]);
      setHistory(h.items || []);
      setAtsTrend(t.items || []);
      setShowHistory(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load resume history");
    }
  }, [id, paid]);

  const restoreVersion = useCallback(async (version) => {
    if (!version?.data || !resume) return;
    try {
      const updated = await updateResume(resume.id, { name: resume.name, template: resume.template, data: version.data });
      setResume(updated);
      setShowHistory(false);
      toast.success("Previous resume version restored");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not restore version");
    }
  }, [resume]);

  const onExport =
    useCallback(() => {
      if (!resume) {
        return;
      }

      try {
        downloadResumePDF(
          resume
        );

        toast.success(
          "PDF downloaded"
        );
      } catch (err) {
        console.error(
          "pdf export failed",
          err
        );

        toast.error(
          "Export failed"
        );
      }
    }, [resume]);

  const onATS =
    useCallback(
      async () => {
        if (!resume) {
          return;
        }

        setAtsBusy(true);

        try {
          const r =
            await scoreATS(
              resume.id
            );

          setAtsResult(r);

          setResume(
            (prev) => ({
              ...prev,
              ats_score:
                r.ats_score,
            })
          );

          toast.success(
            `ATS score: ${r.ats_score}/100`
          );
        } catch (err) {
          console.error(
            "ats failed",
            err
          );

          toast.error(
            err.response?.data
              ?.detail ||
              "ATS analysis failed"
          );
        } finally {
          setAtsBusy(false);
        }
      },
      [resume]
    );

  if (loading) {
    return (
      <Shell>
        <div
          className="text-zinc-500"
          data-testid="rb-edit-loading"
        >
          Loading editor…
        </div>
      </Shell>
    );
  }

  if (!resume) {
    return (
      <Shell>
        <div className="text-zinc-500">Resume not found.</div>
      </Shell>
    );
  }

  if (!paid) {
    return (
      <Shell>
        <UpgradeGate title="Resume Builder is locked" description="Your saved resume is still safe. Upgrade to Pro or Premium to continue editing and use Resume Builder features." />
      </Shell>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#050505]"
      data-testid="resume-editor-page"
    >
      <Navbar />

      <DashSidebar />

      <main className="lg:ml-60 pt-20 sm:pt-24 px-3 sm:px-6 md:px-8 pb-16 max-w-[1500px] mx-auto lg:mx-0">

        {/* =====================================================
            TOP BAR
        ====================================================== */}
        <div className="flex flex-wrap items-center gap-2 mb-4">

          <button
            onClick={() =>
              nav("/resume-builder")
            }
            className="text-zinc-400 hover:text-white inline-flex items-center gap-1.5 text-sm"
            data-testid="rb-back"
          >
            <ArrowLeft size={16} />
            Back
          </button>

          <input
            value={resume.name}
            onChange={(e) =>
              update({
                name: e.target.value,
              })
            }
            placeholder="Untitled Resume"
            data-testid="rb-name-input"
            className="flex-1 min-w-[180px] bg-transparent border-b border-white/10 focus:border-blue-500 px-2 py-1 text-base sm:text-lg outline-none"
          />

          <span
            className="text-xs text-zinc-500"
            data-testid="rb-save-state"
          >
            {saving
              ? "Saving…"
              : dirtyRef.current
              ? "Unsaved"
              : "Saved"}
          </span>

          <div className="hidden sm:flex items-center gap-2">

            <select
              value={
                resume.template
              }
              onChange={(e) =>
                update({
                  template:
                    e.target.value,
                })
              }
              data-testid="rb-template-select"
              className="bg-[#0F0F12] border border-white/10 rounded-md px-3 py-2 text-sm outline-none"
            >
              {Object.entries(
                TEMPLATE_META
              ).map(
                ([templateId, meta]) => (
                  <option
                    key={templateId}
                    value={
                      templateId
                    }
                  >
                    {meta.label}
                  </option>
                )
              )}
            </select>

            <button
              onClick={openHistory}
              data-testid="rb-history-btn"
              className="bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 rounded-md text-sm inline-flex items-center gap-1.5"
            >
              <ClockCounterClockwise size={14} />
              History / ATS trend
            </button>

            <button
              onClick={() =>
                setShowJD(true)
              }
              data-testid="rb-jd-btn"
              className="bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 rounded-md text-sm inline-flex items-center gap-1.5"
            >
              <Target size={14} />
              Tailor to job
            </button>

            <button
              onClick={onATS}
              disabled={atsBusy}
              data-testid="rb-ats-btn"
              className="bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 rounded-md text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <ChartLineUp size={14} />

              {atsBusy
                ? "Scoring…"
                : "ATS score"}
            </button>

            {user?.tier === "premium" ? (
              <button
                onClick={() =>
                  downloadResumeDOCX(resume.id)
                    .then(() => toast.success("ATS-friendly DOCX downloaded"))
                    .catch((err) => toast.error(err.response?.data?.detail || "DOCX export failed"))
                }
                data-testid="rb-export-docx-btn"
                className="bg-white/5 border border-white/10 text-white px-3 py-2 rounded-md text-sm"
              >DOCX</button>
            ) : (
              <button type="button" onClick={() => nav("/pricing")} data-testid="rb-export-docx-locked" className="bg-white/5 border border-amber-500/20 text-amber-300 px-3 py-2 rounded-md text-sm">🔒 DOCX</button>
            )}

            <button
              onClick={onExport}
              data-testid="rb-export-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm inline-flex items-center gap-1.5"
            >
              <DownloadSimple
                size={14}
                weight="bold"
              />
              ATS PDF
            </button>

          </div>
        </div>

        {/* =====================================================
            MOBILE ACTION BAR
        ====================================================== */}
        <div
          className="sm:hidden flex flex-wrap gap-2 mb-4"
          data-testid="rb-mobile-actions"
        >

          <select
            value={resume.template}
            onChange={(e) =>
              update({
                template:
                  e.target.value,
              })
            }
            className="flex-1 bg-[#0F0F12] border border-white/10 rounded-md px-3 py-2 text-sm outline-none"
          >
            {Object.entries(
              TEMPLATE_META
            ).map(
              ([templateId, meta]) => (
                <option
                  key={templateId}
                  value={templateId}
                >
                  {meta.label}
                </option>
              )
            )}
          </select>

          <button
            onClick={() =>
              setMobilePreview(
                (v) => !v
              )
            }
            className="bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm inline-flex items-center gap-1.5"
            data-testid="rb-mobile-preview-toggle"
          >
            <Eye size={14} />

            {mobilePreview
              ? "Edit"
              : "Preview"}
          </button>

          <button
            onClick={onATS}
            disabled={atsBusy}
            className="bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <ChartLineUp size={14} />
            ATS
          </button>

          <button
            onClick={() =>
              setShowJD(true)
            }
            className="bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm inline-flex items-center gap-1.5"
          >
            <Target size={14} />
            Job
          </button>

          <button
            onClick={onExport}
            className="flex-1 bg-blue-600 text-white rounded-md px-3 py-2 text-sm inline-flex items-center justify-center gap-1.5"
          >
            <DownloadSimple
              size={14}
              weight="bold"
            />
            PDF
          </button>

        </div>

        {/* =====================================================
            ATS READINESS
        ====================================================== */}
        <div
          className="mb-4 bg-[#0F0F12] border border-white/10 rounded-xl p-4"
          data-testid="rb-ats-readiness"
        >
          <div className="flex items-center justify-between gap-3">

            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                ATS readiness
              </div>

              <div className="text-2xl font-mono font-medium text-blue-400 mt-1">
                {
                  resume
                    .ats_readiness
                    ?.score ?? 0
                }
                /100
              </div>
            </div>

            <div className="text-xs text-zinc-500 max-w-xl">
              Live content checklist.
              Your exported resume is
              single-column and text-based
              for ATS parsing.
            </div>

          </div>

          <div className="mt-3 h-2 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full bg-blue-500"
              style={{
                width: `${
                  resume
                    .ats_readiness
                    ?.score ?? 0
                }%`,
              }}
            />
          </div>
        </div>

        {/* =====================================================
            MAIN EDITOR GRID
        ====================================================== */}
        <div className="grid lg:grid-cols-[260px_1fr] xl:grid-cols-[260px_minmax(0,1fr)_minmax(0,1fr)] gap-4 lg:gap-6">

          {/* ===================================================
              SECTION NAV
          ==================================================== */}
          <aside
            className="hidden lg:block sticky top-24 self-start h-fit bg-[#0F0F12] border border-white/10 rounded-xl p-2"
            data-testid="rb-sections"
          >
            {SECTIONS.map(
              (section) => (
                <button
                  key={section.id}
                  onClick={() =>
                    setActiveSection(
                      section.id
                    )
                  }
                  data-testid={`rb-section-${section.id}`}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    activeSection ===
                    section.id
                      ? "bg-blue-600/15 text-white border border-blue-500/30"
                      : "text-zinc-400 hover:text-white hover:bg-white/5 border border-transparent"
                  }`}
                >
                  {section.label}
                </button>
              )
            )}

            <button
              onClick={() =>
                setAtsResult(null)
              }
              className="w-full text-xs text-zinc-500 mt-3 px-3 py-2 rounded-md hover:text-zinc-300"
            >
              {resume.ats_score
                ? `ATS: ${resume.ats_score}/100`
                : "Run ATS to score"}
            </button>
          </aside>

          {/* ===================================================
              MOBILE SECTION PILLS
          ==================================================== */}
          <div
            className="lg:hidden -mx-3 px-3 flex gap-2 overflow-x-auto pb-2"
            data-testid="rb-sections-mobile"
          >
            {SECTIONS.map(
              (section) => (
                <button
                  key={section.id}
                  onClick={() =>
                    setActiveSection(
                      section.id
                    )
                  }
                  data-testid={`rb-section-m-${section.id}`}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs ${
                    activeSection ===
                    section.id
                      ? "bg-blue-600 text-white"
                      : "bg-white/5 text-zinc-400 border border-white/10"
                  }`}
                >
                  {section.label}
                </button>
              )
            )}
          </div>

          {/* ===================================================
              EDITOR SECTION
          ==================================================== */}
          <section
            className={`${
              mobilePreview
                ? "hidden lg:block"
                : ""
            } bg-[#0F0F12] border border-white/10 rounded-xl p-4 sm:p-5`}
            data-testid="rb-form"
          >

            {activeSection ===
              "personal" && (
              <PersonalForm
                data={resume.data}
                updateData={
                  updateData
                }
              />
            )}

            {activeSection ===
              "summary" && (
              <SummaryForm
                data={resume.data}
                updateData={
                  updateData
                }
              />
            )}

            {activeSection ===
              "experience" && (
              <ExperienceForm
                data={resume.data}
                updateData={
                  updateData
                }
              />
            )}

            {activeSection ===
              "education" && (
              <EducationForm
                data={resume.data}
                updateData={
                  updateData
                }
              />
            )}

            {activeSection ===
              "skills" && (
              <SkillsForm
                data={resume.data}
                updateData={
                  updateData
                }
              />
            )}

            {activeSection ===
              "certifications" && (
              <ListEditor
                field="certifications"
                label="Certifications"
                make={
                  blankCertification
                }
                data={resume.data}
                updateData={
                  updateData
                }
                fields={[
                  {
                    key: "name",
                    label: "Name",
                  },
                  {
                    key: "issuer",
                    label: "Issuer",
                  },
                  {
                    key: "year",
                    label: "Year",
                  },
                ]}
              />
            )}

            {activeSection ===
              "projects" && (
              <ListEditor
                field="projects"
                label="Projects"
                make={
                  blankProject
                }
                data={resume.data}
                updateData={
                  updateData
                }
                fields={[
                  {
                    key: "name",
                    label: "Name",
                  },
                  {
                    key: "description",
                    label:
                      "Description",
                    textarea:
                      true,
                  },
                  {
                    key: "tech",
                    label:
                      "Tech / stack",
                  },
                  {
                    key: "link",
                    label: "Link",
                  },
                ]}
              />
            )}

            {activeSection ===
              "languages" && (
              <ListEditor
                field="languages"
                label="Languages"
                make={
                  blankLanguage
                }
                data={resume.data}
                updateData={
                  updateData
                }
                fields={[
                  {
                    key: "name",
                    label:
                      "Language",
                  },
                  {
                    key: "level",
                    label:
                      "Level",
                  },
                ]}
              />
            )}

            {/* ATS RESULT */}
            {atsResult && (
              <div
                className="mt-6 bg-blue-500/5 border border-blue-500/20 rounded-lg p-4"
                data-testid="rb-ats-result"
              >
                <div className="flex items-center justify-between gap-3">

                  <div className="text-sm">

                    <div className="text-blue-300 font-medium">
                      ATS score:{" "}
                      <span className="font-mono">
                        {
                          atsResult.ats_score
                        }
                        /100
                      </span>
                    </div>

                    <div className="text-zinc-400 text-xs mt-1">
                      {
                        atsResult.strength
                      }
                    </div>

                  </div>

                  <button
                    onClick={() =>
                      setAtsResult(
                        null
                      )
                    }
                    className="text-zinc-500 hover:text-white text-xs"
                  >
                    Dismiss
                  </button>

                </div>

                {!!(
                  atsResult.missing_keywords ||
                  []
                ).length && (
                  <div className="mt-3">

                    <div className="text-xs text-zinc-500 uppercase tracking-widest mb-1.5">
                      Missing keywords
                    </div>

                    <div className="flex flex-wrap gap-1.5">

                      {atsResult.missing_keywords.map(
                        (keyword) => (
                          <button
                            key={
                              keyword
                            }
                            onClick={() =>
                              updateData({
                                skills:
                                  Array.from(
                                    new Set(
                                      [
                                        ...(resume
                                          .data
                                          .skills ||
                                          []),
                                        keyword,
                                      ]
                                    )
                                  ),
                              })
                            }
                            className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20 hover:bg-amber-500/20"
                            data-testid="rb-ats-add-keyword"
                          >
                            +{" "}
                            {
                              keyword
                            }
                          </button>
                        )
                      )}

                    </div>
                  </div>
                )}

                {!!(
                  atsResult.improvement_suggestions ||
                  []
                ).length && (
                  <ul className="mt-3 text-xs text-zinc-300 space-y-1 list-disc pl-4">

                    {atsResult.improvement_suggestions
                      .slice(
                        0,
                        6
                      )
                      .map(
                        (suggestion) => (
                          <li
                            key={
                              suggestion
                            }
                          >
                            {
                              suggestion
                            }
                          </li>
                        )
                      )}

                  </ul>
                )}

              </div>
            )}

          </section>

          {/* ===================================================
              LIVE PREVIEW
          ==================================================== */}
          <section
            className={`${
              mobilePreview
                ? ""
                : "hidden lg:block"
            } xl:col-span-1`}
            data-testid="rb-preview-wrap"
          >

            <div
              ref={
                previewViewportRef
              }
              className="sticky top-24 bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-950 border border-white/10 rounded-xl p-3 sm:p-4"
              style={{
                maxHeight:
                  "calc(100vh - 6rem)",
                overflowX:
                  "hidden",
                overflowY:
                  "auto",
              }}
            >

              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-semibold mb-2 px-1">
                Live preview ·{" "}
                {
                  TEMPLATE_META[
                    previewResume?.template ||
                      resume.template
                  ]?.label
                }
              </div>

              {/*
               * IMPORTANT:
               *
               * The outer frame owns the scaled width/height.
               * The actual resume remains exactly 210mm x 297mm.
               *
               * This prevents CSS transform from reserving the
               * original unscaled width and clipping half the page.
               */}
              <div
                className="mx-auto relative"
                style={{
                  width: `${
                    210 *
                    previewScale
                  }mm`,
                  minHeight: `${
                    297 *
                    previewScale
                  }mm`,
                }}
                data-testid="rb-preview-scale-frame"
              >

                <div
                  style={{
                    width: "210mm",
                    height: "297mm",
                    transform: `scale(${previewScale})`,
                    transformOrigin:
                      "top left",
                  }}
                >

                  <ResumePreview
                    resume={
                      previewResume ||
                      resume
                    }
                  />

                </div>

              </div>

            </div>

          </section>

        </div>
      </main>

      {showHistory && (
        <div className="fixed inset-0 z-[85] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="rb-history-modal">
          <div className="bg-[#0F0F12] border border-white/10 rounded-2xl max-w-3xl w-full max-h-[88vh] overflow-y-auto p-5 sm:p-7">
            <div className="flex items-center justify-between gap-3 mb-6">
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold">// Resume history</div>
                <h2 className="text-xl sm:text-2xl font-medium mt-1">Versions & ATS trend</h2>
              </div>
              <button onClick={() => setShowHistory(false)} className="text-zinc-400 hover:text-white">Close</button>
            </div>
            <div className="bg-black/20 border border-white/5 rounded-xl p-4 mb-5">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">ATS score history</div>
              {atsTrend.length ? (
                <div className="flex items-end gap-2 h-28">
                  {atsTrend.map((item, i) => (
                    <div key={`${item.created_at}-${i}`} className="flex-1 min-w-0 flex flex-col items-center justify-end gap-1">
                      <div className="text-[10px] font-mono text-blue-300">{item.ats_score}</div>
                      <div className="w-full max-w-10 bg-blue-500/50 rounded-t" style={{ height: `${Math.max(8, Math.min(100, Number(item.ats_score) || 0))}%` }} />
                      <div className="text-[8px] text-zinc-600 truncate w-full text-center">{new Date(item.created_at).toLocaleDateString()}</div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-zinc-600">Run ATS scoring to build your trend.</p>}
            </div>
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Saved previous versions</div>
              {history.length ? history.map((version, i) => (
                <div key={`${version.created_at}-${i}`} className="flex items-center justify-between gap-3 border border-white/5 rounded-lg p-3 bg-white/[0.02]">
                  <div>
                    <div className="text-sm text-white">Version {history.length - i}</div>
                    <div className="text-xs text-zinc-600">{new Date(version.created_at).toLocaleString()}</div>
                  </div>
                  <button onClick={() => restoreVersion(version)} className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md">Restore</button>
                </div>
              )) : <p className="text-sm text-zinc-600">No earlier saved versions yet.</p>}
            </div>
          </div>
        </div>
      )}

      {showJD && (
        <JobMatchModal
          resume={resume}
          onClose={() =>
            setShowJD(false)
          }
          updateData={
            updateData
          }
          setResume={
            setResume
          }
        />
      )}
    </div>
  );
}

/* ============================================================
   LOADING SHELL
============================================================ */

const Shell = ({
  children,
}) => (
  <div className="min-h-screen bg-[#050505]">
    <Navbar />
    <DashSidebar />

    <main className="lg:ml-60 pt-24 px-6 pb-24">
      {children}
    </main>
  </div>
);

/* ============================================================
   PERSONAL FORM
============================================================ */

function FormField({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  testId,
  className = "",
}) {
  return (
    <div className={className}>
      <label className="text-xs tracking-widest uppercase text-zinc-400">
        {label}
      </label>
      <input
        type={type}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-1 w-full bg-[#050505] border border-white/10 focus:border-blue-500 rounded-md px-4 py-3 outline-none text-sm"
      />
    </div>
  );
}

function PersonalForm({
  data,
  updateData,
}) {
  const set = useCallback(
    (key) => (event) => {
      updateData({
        [key]: event.target.value,
      });
    },
    [updateData]
  );

  return (
    <div>
      <SectionHeader
        icon={Briefcase}
        title="Personal information"
      />

      <div className="grid sm:grid-cols-2 gap-4">
        <FormField
          label="Full name"
          value={data.full_name}
          onChange={set("full_name")}
          testId="rb-full_name"
        />

        <FormField
          label="Email"
          value={data.email}
          onChange={set("email")}
          type="email"
          testId="rb-email"
        />

        <FormField
          label="Phone"
          value={data.phone}
          onChange={set("phone")}
          testId="rb-phone"
        />

        <FormField
          label="Location"
          value={data.location}
          onChange={set("location")}
          testId="rb-location"
        />

        <FormField
          label="Target job title"
          value={data.target_role}
          onChange={set("target_role")}
          testId="rb-target_role"
        />

        <FormField
          label="Industry"
          value={data.industry}
          onChange={set("industry")}
          testId="rb-industry"
        />

        <FormField
          className="sm:col-span-2"
          label="LinkedIn URL"
          value={data.linkedin}
          onChange={set("linkedin")}
          testId="rb-linkedin"
        />
      </div>
    </div>
  );
}

/* ============================================================
   SUMMARY
============================================================ */

function SummaryForm({
  data,
  updateData,
}) {
  const [busy, setBusy] =
    useState(false);

  const generate =
    async () => {
      setBusy(true);

      try {
        const r =
          await aiGenerateSummary(
            {
              role:
                data.target_role ||
                data.experience
                  ?.[
                    0
                  ]?.title ||
                "",

              years_experience:
                Math.max(
                  0,
                  (
                    data.experience ||
                    []
                  ).length
                ),

              current_role:
                data.experience
                  ?.[
                    0
                  ]?.title ||
                "",

              skills:
                (
                  data.skills ||
                  []
                ).slice(
                  0,
                  16
                ),

              industry:
                data.industry ||
                "",
            }
          );

        updateData({
          summary:
            r.summary,
        });

        toast.success(
          "Summary generated"
        );
      } catch (err) {
        console.error(
          "summary failed",
          err
        );

        toast.error(
          err.response?.data
            ?.detail ||
            "AI failed"
        );
      } finally {
        setBusy(false);
      }
    };

  const improve =
    async () => {
      if (
        !data.summary?.trim()
      ) {
        return toast.error(
          "Write a draft first"
        );
      }

      setBusy(true);

      try {
        const r =
          await aiImproveText(
            data.summary,
            "summary"
          );

        updateData({
          summary:
            r.improved,
        });

        toast.success(
          "Improved"
        );
      } catch (err) {
        console.error(
          "improve failed",
          err
        );

        toast.error(
          err.response?.data
            ?.detail ||
            "AI failed"
        );
      } finally {
        setBusy(false);
      }
    };

  return (
    <div>
      <SectionHeader
        icon={Sparkle}
        title="Professional summary"
      />

      <textarea
        rows={6}
        value={
          data.summary || ""
        }
        onChange={(e) =>
          updateData({
            summary:
              e.target.value,
          })
        }
        placeholder="Senior engineer with 6+ years building scalable backend systems..."
        data-testid="rb-summary"
        className="w-full bg-[#050505] border border-white/10 focus:border-blue-500 rounded-md px-4 py-3 outline-none text-sm leading-relaxed"
      />

      <div className="flex gap-2 mt-3">

        <button
          onClick={generate}
          disabled={busy}
          data-testid="rb-summary-generate"
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm inline-flex items-center gap-1.5"
        >
          <MagicWand
            size={14}
            weight="fill"
          />

          {busy
            ? "Working…"
            : "Generate with AI"}
        </button>

        <button
          onClick={improve}
          disabled={busy}
          data-testid="rb-summary-improve"
          className="bg-white/5 hover:bg-white/10 border border-white/10 text-white px-4 py-2 rounded-md text-sm inline-flex items-center gap-1.5"
        >
          <Sparkle size={14} />
          Improve mine
        </button>

      </div>
    </div>
  );
}

/* ============================================================
   EXPERIENCE
============================================================ */

function ExperienceForm({
  data,
  updateData,
}) {
  const items =
    data.experience ||
    [];

  const setItems =
    (next) =>
      updateData({
        experience:
          next,
      });

  const add = () =>
    setItems([
      ...items,
      blankExperience(),
    ]);

  const remove =
    (index) =>
      setItems(
        items.filter(
          (_, i) =>
            i !== index
        )
      );

  const patch =
    (index, patchData) =>
      setItems(
        items.map(
          (
            item,
            i
          ) =>
            i === index
              ? {
                  ...item,
                  ...patchData,
                }
              : item
        )
      );

  return (
    <div>
      <SectionHeader
        icon={Briefcase}
        title="Work experience"
      >
        <button
          onClick={add}
          data-testid="rb-exp-add"
          className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md inline-flex items-center gap-1"
        >
          <Plus
            size={12}
            weight="bold"
          />
          Add role
        </button>
      </SectionHeader>

      <div className="space-y-4">

        {items.map(
          (
            item,
            index
          ) => (
            <ExperienceItem
              key={index}
              idx={index}
              item={item}
              onChange={(
                patchData
              ) =>
                patch(
                  index,
                  patchData
                )
              }
              onRemove={() =>
                remove(index)
              }
            />
          )
        )}

        {items.length ===
          0 && (
          <p className="text-sm text-zinc-500">
            Add your first role
            to get AI-generated
            bullet points.
          </p>
        )}

      </div>
    </div>
  );
}

function ExperienceItem({
  idx,
  item,
  onChange,
  onRemove,
}) {
  const [busy, setBusy] =
    useState(false);

  const [ctx, setCtx] =
    useState("");

  const generateBullets =
    async () => {
      if (!item.title) {
        return toast.error(
          "Add a job title first"
        );
      }

      setBusy(true);

      try {
        const r =
          await aiGenerateBullets(
            {
              title:
                item.title,

              company:
                item.company ||
                "",

              context: ctx,

              existing_bullets:
                item.bullets ||
                [],
            }
          );

        onChange({
          bullets:
            r.bullets,
        });

        toast.success(
          `${r.bullets.length} bullet points generated`
        );
      } catch (err) {
        console.error(
          "bullets failed",
          err
        );

        toast.error(
          err.response?.data
            ?.detail ||
            "AI failed"
        );
      } finally {
        setBusy(false);
      }
    };


  const setBullet =
    (index, value) => {
      const next = [
        ...(item.bullets ||
          []),
      ];

      next[index] =
        value;

      onChange({
        bullets: next,
      });
    };

  const addBullet =
    () =>
      onChange({
        bullets: [
          ...(item.bullets ||
            []),
          "",
        ],
      });

  const removeBullet =
    (index) =>
      onChange({
        bullets: (
          item.bullets ||
          []
        ).filter(
          (_, i) =>
            i !== index
        ),
      });

  return (
    <div
      className="bg-white/[0.02] border border-white/5 rounded-lg p-4"
      data-testid={`rb-exp-item-${idx}`}
    >

      <div className="flex items-start justify-between gap-2 mb-3">

        <div className="text-sm font-medium text-zinc-300">
          Role #{idx + 1}
        </div>

        <button
          onClick={
            onRemove
          }
          className="text-zinc-500 hover:text-red-300"
          data-testid={`rb-exp-${idx}-remove`}
        >
          <X size={16} />
        </button>

      </div>

      <div className="grid sm:grid-cols-2 gap-3">

        <FormField
          label="Job title"
          value={item.title}
          onChange={(e) => onChange({ title: e.target.value })}
          testId={`rb-exp-${idx}-title`}
        />

        <FormField
          label="Company"
          value={item.company}
          onChange={(e) => onChange({ company: e.target.value })}
          testId={`rb-exp-${idx}-company`}
        />

        <FormField
          label="Location"
          value={item.location}
          onChange={(e) => onChange({ location: e.target.value })}
          testId={`rb-exp-${idx}-location`}
        />

        <div className="flex items-end gap-2">

          <FormField
            label="Start"
            value={item.start}
            onChange={(e) => onChange({ start: e.target.value })}
            testId={`rb-exp-${idx}-start`}
          />

          <FormField
            label="End"
            value={item.end}
            onChange={(e) => onChange({ end: e.target.value })}
            placeholder={item.current ? "" : ""}
            testId={`rb-exp-${idx}-end`}
          />

        </div>

        <label className="inline-flex items-center gap-2 text-xs text-zinc-400">

          <input
            type="checkbox"
            checked={
              !!item.current
            }
            onChange={(e) =>
              onChange({
                current:
                  e.target
                    .checked,

                end: e.target
                  .checked
                  ? ""
                  : item.end,
              })
            }
            data-testid={`rb-exp-${idx}-current`}
          />

          I currently work here

        </label>

      </div>

      <div className="mt-4">

        <div className="text-[11px] tracking-widest uppercase text-zinc-500 mb-1">
          Bullet points
        </div>

        <div className="space-y-2">

          {(
            item.bullets ||
            []
          ).map(
            (
              bullet,
              bulletIndex
            ) => (
              <div
                key={
                  bulletIndex
                }
                className="flex gap-2 items-start"
              >

                <span className="text-blue-400 mt-2">
                  •
                </span>

                <textarea
                  rows={2}
                  value={bullet}
                  onChange={(e) =>
                    setBullet(
                      bulletIndex,
                      e.target
                        .value
                    )
                  }
                  data-testid={`rb-exp-${idx}-bullet-${bulletIndex}`}
                  className="flex-1 bg-[#050505] border border-white/10 focus:border-blue-500 rounded-md px-3 py-2 outline-none text-sm"
                />

                <button
                  onClick={() =>
                    removeBullet(
                      bulletIndex
                    )
                  }
                  className="text-zinc-500 hover:text-red-300 mt-2"
                >
                  <X size={14} />
                </button>

              </div>
            )
          )}

          <button
            onClick={
              addBullet
            }
            data-testid={`rb-exp-${idx}-add-bullet`}
            className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
          >
            <Plus size={12} />
            Add bullet
          </button>

        </div>

        <div className="mt-3 grid sm:grid-cols-[1fr_auto] gap-2">

          <input
            value={ctx}
            onChange={(e) =>
              setCtx(
                e.target.value
              )
            }
            placeholder="Short context (e.g. Built payment microservice handling 2M req/day)"
            data-testid={`rb-exp-${idx}-ctx`}
            className="bg-[#050505] border border-white/10 focus:border-blue-500 rounded-md px-3 py-2 outline-none text-sm"
          />

          <button
            onClick={
              generateBullets
            }
            disabled={busy}
            data-testid={`rb-exp-${idx}-generate`}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-2 rounded-md text-sm inline-flex items-center justify-center gap-1.5"
          >
            <MagicWand
              size={14}
              weight="fill"
            />

            {busy
              ? "…"
              : "AI bullets"}
          </button>

        </div>

      </div>
    </div>
  );
}

/* ============================================================
   EDUCATION
============================================================ */

function EducationForm({
  data,
  updateData,
}) {
  return (
    <ListEditor
      field="education"
      label="Education"
      make={
        blankEducation
      }
      data={data}
      updateData={
        updateData
      }
      icon={
        GraduationCap
      }
      fields={[
        {
          key: "degree",
          label: "Degree",
        },
        {
          key: "school",
          label: "School",
        },
        {
          key: "location",
          label: "Location",
        },
        {
          key: "start",
          label: "Start",
        },
        {
          key: "end",
          label: "End",
        },
        {
          key: "gpa",
          label:
            "GPA / honors",
        },
      ]}
    />
  );
}

/* ============================================================
   SKILLS
============================================================ */

function SkillsForm({
  data,
  updateData,
}) {
  const [draft, setDraft] =
    useState("");

  const [busy, setBusy] =
    useState(false);

  const skills =
    data.skills || [];

  const role =
    data.experience?.[
      0
    ]?.title ||
    data.summary?.slice(
      0,
      80
    ) ||
    "";

  const add =
    () => {
      const next =
        draft
          .split(",")
          .map(
            (skill) =>
              skill.trim()
          )
          .filter(Boolean);

      if (!next.length) {
        return;
      }

      updateData({
        skills:
          Array.from(
            new Set([
              ...skills,
              ...next,
            ])
          ),
      });

      setDraft("");
    };

  const remove =
    (skill) =>
      updateData({
        skills:
          skills.filter(
            (item) =>
              item !== skill
          ),
      });

  const suggest =
    async () => {
      if (!role.trim()) {
        return toast.error(
          "Add at least one job title first"
        );
      }

      setBusy(true);

      try {
        const r =
          await aiSuggestSkills(
            {
              role,
              industry:
                data.industry ||
                "",
              current_skills:
                skills,
            }
          );

        updateData({
          skills:
            Array.from(
              new Set([
                ...skills,
                ...r.skills,
              ])
            ),
        });

        toast.success(
          `Added ${r.skills.length} skill suggestions`
        );
      } catch (err) {
        console.error(
          "skills failed",
          err
        );

        toast.error(
          err.response?.data
            ?.detail ||
            "AI failed"
        );
      } finally {
        setBusy(false);
      }
    };

  return (
    <div>

      <SectionHeader
        icon={Wrench}
        title="Skills"
      >
        <button
          onClick={
            suggest
          }
          disabled={busy}
          data-testid="rb-skills-suggest"
          className="text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-md inline-flex items-center gap-1"
        >
          <MagicWand
            size={12}
            weight="fill"
          />
          AI suggest
        </button>
      </SectionHeader>

      <div className="flex gap-2 mb-3">

        <input
          value={draft}
          onChange={(e) =>
            setDraft(
              e.target.value
            )
          }
          onKeyDown={(e) => {
            if (
              e.key ===
              "Enter"
            ) {
              e.preventDefault();
              add();
            }
          }}
          placeholder="React, Python, AWS… (comma-separated, Enter to add)"
          data-testid="rb-skills-input"
          className="flex-1 bg-[#050505] border border-white/10 focus:border-blue-500 rounded-md px-3 py-2 outline-none text-sm"
        />

        <button
          onClick={add}
          className="bg-white/5 hover:bg-white/10 border border-white/10 text-white px-3 py-2 rounded-md text-sm"
          data-testid="rb-skills-add"
        >
          Add
        </button>

      </div>

      <div
        className="flex flex-wrap gap-2"
        data-testid="rb-skills-list"
      >

        {skills.map(
          (skill) => (
            <span
              key={skill}
              className="inline-flex items-center gap-1.5 bg-blue-500/10 border border-blue-500/30 text-blue-200 text-xs px-2 py-1 rounded"
            >
              {skill}

              <button
                onClick={() =>
                  remove(skill)
                }
                className="text-blue-300 hover:text-white"
              >
                <X size={12} />
              </button>

            </span>
          )
        )}

        {!skills.length && (
          <p className="text-sm text-zinc-500">
            No skills yet —
            type them above or
            click{" "}
            <span className="text-blue-400">
              AI suggest
            </span>
            .
          </p>
        )}

      </div>
    </div>
  );
}

/* ============================================================
   GENERIC LIST EDITOR
============================================================ */

function ListEditor({
  field,
  label,
  make,
  data,
  updateData,
  fields,
  icon = Certificate,
}) {
  const items =
    data[field] || [];

  const setItems =
    (next) =>
      updateData({
        [field]:
          next,
      });

  const add =
    () =>
      setItems([
        ...items,
        make(),
      ]);

  const remove =
    (index) =>
      setItems(
        items.filter(
          (_, i) =>
            i !== index
        )
      );

  const patch =
    (index, patchData) =>
      setItems(
        items.map(
          (
            item,
            i
          ) =>
            i === index
              ? {
                  ...item,
                  ...patchData,
                }
              : item
        )
      );

  return (
    <div>

      <SectionHeader
        icon={icon}
        title={label}
      >
        <button
          onClick={add}
          data-testid={`rb-${field}-add`}
          className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md inline-flex items-center gap-1"
        >
          <Plus
            size={12}
            weight="bold"
          />
          Add
        </button>
      </SectionHeader>

      <div className="space-y-3">

        {items.map(
          (
            item,
            index
          ) => (
            <div
              key={index}
              className="bg-white/[0.02] border border-white/5 rounded-lg p-4"
              data-testid={`rb-${field}-item-${index}`}
            >

              <div className="flex items-start justify-between gap-2 mb-2">

                <div className="text-sm font-medium text-zinc-300">
                  {label.replace(
                    /s$/,
                    ""
                  )}{" "}
                  #{index + 1}
                </div>

                <button
                  onClick={() =>
                    remove(
                      index
                    )
                  }
                  className="text-zinc-500 hover:text-red-300"
                  data-testid={`rb-${field}-${index}-remove`}
                >
                  <X size={14} />
                </button>

              </div>

              <div className="grid sm:grid-cols-2 gap-3">

                {fields.map(
                  (fieldConfig) => (
                    <div
                      key={
                        fieldConfig.key
                      }
                      className={
                        fieldConfig.textarea
                          ? "sm:col-span-2"
                          : ""
                      }
                    >

                      <label className="text-[11px] tracking-widest uppercase text-zinc-500">
                        {
                          fieldConfig.label
                        }
                      </label>

                      {fieldConfig.textarea ? (
                        <textarea
                          rows={3}
                          value={
                            item[
                              fieldConfig
                                .key
                            ] ||
                            ""
                          }
                          onChange={(
                            e
                          ) =>
                            patch(
                              index,
                              {
                                [fieldConfig.key]:
                                  e.target
                                    .value,
                              }
                            )
                          }
                          data-testid={`rb-${field}-${index}-${fieldConfig.key}`}
                          className="mt-1 w-full bg-[#050505] border border-white/10 focus:border-blue-500 rounded-md px-3 py-2 outline-none text-sm"
                        />
                      ) : (
                        <input
                          value={
                            item[
                              fieldConfig
                                .key
                            ] ||
                            ""
                          }
                          onChange={(
                            e
                          ) =>
                            patch(
                              index,
                              {
                                [fieldConfig.key]:
                                  e.target
                                    .value,
                              }
                            )
                          }
                          data-testid={`rb-${field}-${index}-${fieldConfig.key}`}
                          className="mt-1 w-full bg-[#050505] border border-white/10 focus:border-blue-500 rounded-md px-3 py-2 outline-none text-sm"
                        />
                      )}

                    </div>
                  )
                )}

              </div>
            </div>
          )
        )}

        {!items.length && (
          <p className="text-sm text-zinc-500">
            No{" "}
            {label.toLowerCase()}{" "}
            yet.
          </p>
        )}

      </div>
    </div>
  );
}

/* ============================================================
   SECTION HEADER
============================================================ */

const SectionHeader = ({
  icon: Icon,
  title,
  children,
}) => (
  <div className="flex items-center justify-between gap-2 mb-4">

    <div className="flex items-center gap-2">

      <Icon
        size={18}
        weight="duotone"
        className="text-blue-400"
      />

      <h2 className="text-lg sm:text-xl font-medium">
        {title}
      </h2>

    </div>

    {children}

  </div>
);

/* ============================================================
   JOB MATCH MODAL
============================================================ */

function JobMatchModal({
  resume,
  onClose,
  updateData,
  setResume,
}) {
  const [jd, setJd] =
    useState(
      resume.last_jd ||
        ""
    );

  const [busy, setBusy] =
    useState(false);

  const [result, setResult] =
    useState(null);

  const run =
    async () => {
      if (
        jd.trim().length <
        60
      ) {
        return toast.error(
          "Paste at least 60 characters of the JD."
        );
      }

      setBusy(true);

      try {
        const r =
          await optimizeForJob(
            resume.id,
            jd
          );

        setResult(r);

        setResume(
          (prev) => ({
            ...prev,
            job_match_score:
              r.match_score,
            last_jd: jd,
          })
        );

        toast.success(
          `Match score: ${r.match_score}/100`
        );
      } catch (err) {
        console.error(
          "jd match failed",
          err
        );

        toast.error(
          err.response?.data
            ?.detail ||
            "AI failed"
        );
      } finally {
        setBusy(false);
      }
    };

  const applySummary =
    () => {
      if (
        !result
          ?.customized_summary
      ) {
        return;
      }

      updateData({
        summary:
          result.customized_summary,
      });

      toast.success(
        "Summary updated"
      );
    };

  const applySkills =
    () => {
      if (
        !result
          ?.matching_skills
          ?.length
      ) {
        return;
      }

      updateData({
        skills:
          Array.from(
            new Set([
              ...(resume
                .data
                .skills ||
                []),
              ...result.matching_skills,
            ])
          ),
      });

      toast.success(
        "Skills merged in"
      );
    };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="rb-jd-modal"
    >

      <div className="bg-[#0F0F12] border border-white/10 rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto p-5 sm:p-7">

        <div className="flex items-center justify-between gap-3 mb-4">

          <div>

            <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold">
              // Job-specific optimization
            </div>

            <h2 className="text-xl sm:text-2xl font-medium mt-1">
              Tailor this resume
              to a job
            </h2>

          </div>

          <button
            onClick={
              onClose
            }
            className="text-zinc-400 hover:text-white"
            data-testid="rb-jd-close"
          >
            Close
          </button>

        </div>

        <p className="text-sm text-zinc-400">
          Paste the full job
          description. AI extracts
          must-have keywords,
          scores your match, and
          proposes a customised
          summary + bullets.
        </p>

        <textarea
          value={jd}
          onChange={(e) =>
            setJd(
              e.target.value
            )
          }
          rows={8}
          placeholder="Paste job description here…"
          data-testid="rb-jd-textarea"
          className="mt-4 w-full bg-[#050505] border border-white/10 focus:border-blue-500 rounded-md px-4 py-3 outline-none text-sm leading-relaxed"
        />

        <div className="mt-3 flex justify-end gap-2">

          <button
            onClick={
              onClose
            }
            className="bg-white/5 border border-white/10 text-white px-4 py-2 rounded-md text-sm"
          >
            Cancel
          </button>

          <button
            onClick={run}
            disabled={busy}
            data-testid="rb-jd-run"
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm inline-flex items-center gap-1.5"
          >
            <Target size={14} />

            {busy
              ? "Analyzing…"
              : "Analyze & match"}
          </button>

        </div>

        {result && (
          <div
            className="mt-6 space-y-4"
            data-testid="rb-jd-result"
          >

            {/* MATCH SCORE */}
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">

              <div>

                <div className="text-xs text-zinc-400 uppercase tracking-widest">
                  Job match
                </div>

                <div
                  className={`text-3xl font-medium font-mono ${
                    result.match_score >=
                    80
                      ? "text-emerald-300"
                      : result.match_score >=
                        60
                      ? "text-blue-300"
                      : "text-amber-300"
                  }`}
                >
                  {
                    result.match_score
                  }

                  <span className="text-zinc-500 text-base">
                    /100
                  </span>
                </div>

              </div>

              <div className="text-sm text-zinc-300 max-w-md">
                {
                  result.notes
                }
              </div>

            </div>

            {/* SUMMARY */}
            {result.customized_summary && (
              <div className="bg-[#050505] border border-white/10 rounded-lg p-4">

                <div className="flex items-center justify-between">

                  <div className="text-xs text-zinc-400 uppercase tracking-widest">
                    Suggested summary
                  </div>

                  <button
                    onClick={
                      applySummary
                    }
                    className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                    data-testid="rb-jd-apply-summary"
                  >
                    Apply
                  </button>

                </div>

                <p className="text-sm text-zinc-200 mt-2 leading-relaxed">
                  {
                    result.customized_summary
                  }
                </p>

              </div>
            )}

            {/* MATCHING SKILLS */}
            {!!(
              result.matching_skills ||
              []
            ).length && (
              <div className="bg-[#050505] border border-white/10 rounded-lg p-4">

                <div className="flex items-center justify-between">

                  <div className="text-xs text-zinc-400 uppercase tracking-widest">
                    Matching skills
                    (from JD)
                  </div>

                  <button
                    onClick={
                      applySkills
                    }
                    className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                    data-testid="rb-jd-apply-skills"
                  >
                    Add to skills
                  </button>

                </div>

                <div className="flex flex-wrap gap-1.5 mt-2">

                  {result.matching_skills.map(
                    (skill) => (
                      <span
                        key={skill}
                        className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20"
                      >
                        {skill}
                      </span>
                    )
                  )}

                </div>

              </div>
            )}

            {/* MISSING KEYWORDS */}
            {!!(
              result.missing_keywords ||
              []
            ).length && (
              <div className="bg-[#050505] border border-white/10 rounded-lg p-4">

                <div className="text-xs text-zinc-400 uppercase tracking-widest mb-2">
                  Missing keywords
                </div>

                <div className="flex flex-wrap gap-1.5">

                  {result.missing_keywords.map(
                    (keyword) => (
                      <span
                        key={keyword}
                        className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20"
                      >
                        {keyword}
                      </span>
                    )
                  )}

                </div>

              </div>
            )}

            {/* SUGGESTED BULLETS */}
            {!!(
              result.suggested_bullets ||
              []
            ).length && (
              <div className="bg-[#050505] border border-white/10 rounded-lg p-4">

                <div className="text-xs text-zinc-400 uppercase tracking-widest mb-2">
                  Suggested bullets
                </div>

                <ul className="list-disc pl-5 text-sm text-zinc-200 space-y-1">

                  {result.suggested_bullets.map(
                    (bullet) => (
                      <li
                        key={bullet}
                      >
                        {bullet}
                      </li>
                    )
                  )}

                </ul>

              </div>
            )}

          </div>
        )}

      </div>
    </div>
  );
}
